import { createIntl, createIntlCache } from 'react-intl';
import { get } from 'lodash';
import { catchError } from '@utils/sentry';
import enTranslationMessages from '../locale/en.json';
import esTranslationMessages from '../locale/es.json';
import frTranslationMessages from '../locale/fr.json';
import ptTranslationMessages from '../locale/pt.json';
export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'pt'];
const mergeWithEnglish = (messages) => Object.entries(enTranslationMessages).reduce((acc, [id, defaultMessage]) => {
    const localized = messages?.[id];
    acc[id] = typeof localized === 'string' && localized.trim().length > 0 ? localized : defaultMessage;
    return acc;
}, {});
export const translations = {
    en: mergeWithEnglish(enTranslationMessages),
    es: mergeWithEnglish(esTranslationMessages),
    fr: mergeWithEnglish(frTranslationMessages),
    pt: mergeWithEnglish(ptTranslationMessages),
};
const DEFAULT_LOCALE_CONSTANT = 'en';
const normalizeLocale = (value) => {
    if (!value) {
        return null;
    }
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) {
        return null;
    }
    if (SUPPORTED_LOCALES.includes(trimmed)) {
        return trimmed;
    }
    const base = trimmed.split('-')[0];
    if (SUPPORTED_LOCALES.includes(base)) {
        return base;
    }
    return null;
};
const resolveSystemLocale = () => {
    if (typeof navigator !== 'undefined') {
        const primaryCandidate = Array.isArray(navigator.languages) && navigator.languages.length > 0
            ? navigator.languages[0]
            : navigator.language;
        const normalized = normalizeLocale(primaryCandidate);
        if (normalized) {
            return normalized;
        }
    }
    return null;
};
export const defaultLocale = () => {
    const systemLocale = resolveSystemLocale();
    if (systemLocale) {
        if (process.env.NODE_ENV !== 'production') {
            console.info('[i18n] using browser locale', { locale: systemLocale });
        }
        return systemLocale;
    }
    try {
        if (process.env.NODE_ENV !== 'production') {
            console.info('[i18n] falling back to default locale', { locale: DEFAULT_LOCALE_CONSTANT });
        }
        return DEFAULT_LOCALE_CONSTANT;
    }
    catch (error) {
        catchError({
            title: 'An error occurred in defaultLocale function',
            error: error,
        });
        return DEFAULT_LOCALE_CONSTANT;
    }
};
export const DEFAULT_LOCALE = defaultLocale();
export const cache = createIntlCache();
const handleIntlError = (error) => {
    if (error.code === 'MISSING_TRANSLATION') {
        return;
    }
    if (process.env.NODE_ENV !== 'production') {
        console.error('[i18n] Intl error:', error);
    }
};
const createIntlInstance = (locale) => createIntl({
    locale,
    messages: translations[locale],
    onError: handleIntlError,
}, cache);
export let intl = createIntlInstance(DEFAULT_LOCALE);
if (typeof document !== 'undefined') {
    document.documentElement.lang = DEFAULT_LOCALE;
}
export let fmt = intl.formatMessage;
export const changeLanguage = (newLocale) => {
    try {
        const parsedLocale = normalizeLocale(newLocale) ?? (get(translations, newLocale) ? newLocale : DEFAULT_LOCALE);
        intl = createIntlInstance(parsedLocale);
        fmt = intl.formatMessage;
        if (typeof document !== 'undefined') {
            document.documentElement.lang = parsedLocale;
        }
        if (process.env.NODE_ENV !== 'production') {
            console.info('[i18n] locale changed', { locale: parsedLocale });
        }
        return intl;
    }
    catch (error) {
        catchError({
            title: 'An error occurred in changeLanguage function',
            error: error,
        });
        return intl;
    }
};
export default intl;
